import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * "Más": las acciones SECUNDARIAS de una fila o de una sección, en un menú con el estilo de la app.
 *
 * Por qué existe: "hay secciones que ya tienen un buen de botoncitos y se comienza a sentir abrumador". Cada fila de
 * una tabla traía tres o cuatro botones del mismo tamaño (Editar · Desactivar · Copiar · Extender…) y nada decía cuál
 * era el importante. La regla: UNA acción principal a la vista (la que más se usa) y lo demás aquí. Nada se quitó.
 *
 * Cómo se porta: botón "⋯ Más" (o el texto que se pase) con `aria-haspopup="menu"` y `aria-expanded`; el menú es
 * `role="menu"` con `role="menuitem"`, el foco entra al primero; ↑ ↓ Inicio Fin mueven, Enter/Espacio eligen, Escape
 * cierra y regresa el foco al botón (y NO cierra la ventana que esté detrás), Tab o tocar fuera lo cierran. Va en un
 * PORTAL, pegado al botón: una tabla con scroll de lado no lo recorta; se abre hacia arriba si no cabe abajo y nunca
 * pasa de la pantalla. z-index 1200: encima de las ventanas de edición (60), la galería (70), "Ampliar" (1000) y las
 * ventanas de detalle (1100); debajo de los diálogos de la app (5000), que es lo que abre un "Desactivar" con peligro.
 * Al elegir, primero se cierra el menú y luego corre la acción.
 */
export interface ItemMas {
  etiqueta: ReactNode;
  onClick: () => void | Promise<void>;
  /** Lo que quita, borra o desactiva: en rojo (y el diálogo que abra, con `peligro`). */
  peligro?: boolean;
  disabled?: boolean;
  title?: string;
  /** Para las pruebas y las ligas: `data-accion`. */
  accion?: string;
}

const CSS = `
.mas-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; font-size: 12.5px; box-shadow: none; white-space: nowrap; }
.mas-btn[aria-expanded="true"] { border-color: var(--gold); background: var(--gold-light); color: var(--gold-dark); }
.mas-menu { position: fixed; z-index: 1200; min-width: 190px; max-width: min(320px, calc(100vw - 16px)); padding: 6px; border-radius: 14px;
  background: #fff; border: 1px solid var(--border); box-shadow: 0 12px 32px rgba(40,33,40,.16), 0 2px 6px rgba(40,33,40,.08); display: grid; gap: 2px; }
.mas-menu [role="menuitem"] { all: unset; box-sizing: border-box; display: block; width: 100%; padding: 9px 12px; border-radius: 10px; font: inherit; font-size: 13.5px;
  color: var(--ink, #2d2a2e); cursor: pointer; line-height: 1.3; }
.mas-menu [role="menuitem"]:hover, .mas-menu [role="menuitem"]:focus-visible { background: var(--gold-light); outline: none; }
.mas-menu [role="menuitem"][data-peligro] { color: var(--danger, #b42318); }
.mas-menu [role="menuitem"][data-peligro]:hover, .mas-menu [role="menuitem"][data-peligro]:focus-visible { background: #fdecea; }
.mas-menu [role="menuitem"][aria-disabled="true"] { opacity: .45; cursor: default; }
.mas-menu hr { border: none; border-top: 1px solid var(--border); margin: 4px 6px; }
`;
let cssPuesto = false;
function ponerCss() {
  if (cssPuesto || typeof document === 'undefined') return;
  const s = document.createElement('style'); s.setAttribute('data-mas-menu-css', ''); s.textContent = CSS; document.head.appendChild(s); cssPuesto = true;
}

export function MasMenu({ items, etiqueta = 'Más', ariaLabel, estilo, datos }: {
  items: (ItemMas | null | false | undefined)[]; etiqueta?: ReactNode; ariaLabel?: string; estilo?: React.CSSProperties; datos?: string;
}) {
  ponerCss();
  const lista = items.filter(Boolean) as ItemMas[];
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const cerrar = (foco = true) => { setAbierto(false); setPos(null); if (foco) btn.current?.focus(); };
  const colocar = () => {
    const b = btn.current?.getBoundingClientRect(); const m = menu.current;
    if (!b || !m) return;
    const w = m.offsetWidth, h = m.offsetHeight, vw = window.innerWidth, vh = window.innerHeight;
    let left = b.right - w; if (left < 8) left = Math.min(b.left, vw - w - 8); left = Math.max(8, Math.min(left, vw - w - 8));
    let top = b.bottom + 6; if (top + h > vh - 8 && b.top - h - 6 >= 8) top = b.top - h - 6; top = Math.max(8, Math.min(top, vh - h - 8));
    setPos({ top, left });
  };
  useLayoutEffect(() => { if (abierto) colocar(); }, [abierto]);
  // El foco entra al primer elemento cuando el menú YA está en su lugar (escondido no puede recibir el foco).
  const enfocado = useRef(false);
  useEffect(() => {
    if (!abierto) { enfocado.current = false; return; }
    if (!pos || enfocado.current) return;
    enfocado.current = true;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus();
  }, [abierto, pos]);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (menu.current?.contains(t) || btn.current?.contains(t)) return;
      cerrar(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (document.querySelector('[data-dialogo]')) return;
      const its = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') || [])];
      const i = its.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); cerrar(); }
      else if (e.key === 'Tab') cerrar(false);
      else if (e.key === 'ArrowDown') { e.preventDefault(); its[(i + 1) % its.length]?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); its[(i - 1 + its.length) % its.length]?.focus(); }
      else if (e.key === 'Home') { e.preventDefault(); its[0]?.focus(); }
      else if (e.key === 'End') { e.preventDefault(); its[its.length - 1]?.focus(); }
    };
    const mover = () => colocar();
    document.addEventListener('mousedown', fuera, true);
    document.addEventListener('touchstart', fuera, true);
    window.addEventListener('keydown', tecla, true);
    window.addEventListener('resize', mover);
    window.addEventListener('scroll', mover, true);
    return () => {
      document.removeEventListener('mousedown', fuera, true);
      document.removeEventListener('touchstart', fuera, true);
      window.removeEventListener('keydown', tecla, true);
      window.removeEventListener('resize', mover);
      window.removeEventListener('scroll', mover, true);
    };
  }, [abierto]);

  if (!lista.length) return null;
  const elegir = (it: ItemMas) => { if (it.disabled) return; cerrar(false); void it.onClick(); };
  return (
    <>
      <button ref={btn} type="button" className="btn-ghost mas-btn" aria-haspopup="menu" aria-expanded={abierto} aria-label={ariaLabel}
        data-mas={datos || ''} style={estilo} onClick={() => (abierto ? cerrar(false) : setAbierto(true))}>
        <span aria-hidden="true" style={{ letterSpacing: 1, fontWeight: 700, lineHeight: 1 }}>⋯</span>{etiqueta ? <span>{etiqueta}</span> : null}
      </button>
      {abierto && createPortal(
        <div ref={menu} role="menu" className="mas-menu" aria-label={ariaLabel || 'Más acciones'} data-mas-menu={datos || ''}
          style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}>
          {lista.map((it, i) => (
            <button key={i} type="button" role="menuitem" tabIndex={-1} aria-disabled={it.disabled || undefined} data-peligro={it.peligro || undefined}
              data-accion={it.accion} title={it.title} onClick={() => elegir(it)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); elegir(it); } }}>
              {it.etiqueta}
            </button>
          ))}
        </div>, document.body)}
    </>
  );
}
